import React, { Suspense, lazy, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ExclamationCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBatteryFull, faPlug, faSolarPanel } from '@fortawesome/free-solid-svg-icons'
import * as api from '../../api/rynek'
import { zwykleCeny as pobierzZwykleCeny } from '../../api/zwykleCeny'
import {
  BUDYNKI_RYNKU, DACHY_RYNKU, LICZBY_OFERT, MAKS_KOMENTARZA, SPRZET_KONKRETNY, SPRZET_ZLECENIA, TERMINY, TRESC_ZGODY,
  TRYB_PRZETARGU, TYPY_OGLOSZENIA, sprzetZWejscia,
} from '../../config/rynek'
import { wierszeOpisu, zl } from '../../lib/rynek'
import { formatPola, kwotaZPola } from '../../lib/zakresBudzetu'
import { zdarzenie } from '../../lib/pomiar'
import Ikona from './IkonaZlecenia'
import BudzetZlecenia from './BudzetZlecenia'
import OknoZlecenia from './OknoZlecenia'
import OknoMiejsc from '../teczka/OknoMiejsc'
import KontoGoscia, { PUSTE_KONTO, brakiKonta } from './KontoGoscia'
import { useAuth } from '../../context/AuthContext'
import WyborKafla from './WyborKafla'
import WyborWojewodztwa from './WyborWojewodztwa'
import SprzetZlecenia from './SprzetZlecenia'
import SprzetKlienta from './SprzetKlienta'
import { PUSTY_SPRZET, maOpisSprzetu, opisSprzetuKlienta } from '../../lib/sprzetKlienta'
import { KONSTRUKCJE, obrazekKonstrukcji } from '../../lib/konstrukcja'
import { PodgladDlaWykonawcow } from '../teczka/DolaczTeczke'
import { useTeczka } from '../../hooks/useTeczka'
import { normalizujMiejsca } from '../../lib/teczka/miejsca'
import { ogloszenieZTeczki } from '../../lib/teczka/dlaWykonawcy'
import { REGIONY } from '../wykonawcy/regionyPolski'
import './Zlecenie.css'

// Droga „Kilka pytań”: prosty dobór w trybie zlecenia (P4). Ładowany dopiero przy tej drodze, żeby /rynek z Projektem 3D
// albo z zestawu nie ciągnął całego kreatora.
const KilkaPytan = lazy(() => import('../../pages/DoborZestawuProsty'))

/**
 * Formularz „Wystaw zlecenie na Rynku” (makieta „VoltValue dla domu” 12, pakiet P3; od 2026-10-29 trzy drogi).
 *
 * Trzy drogi (decyzje właściciela 2026-10-26 i 2026-10-29, VV `second-brain/05`): „Kilka pytań” (domyślna), „Wiem,
 * czego chcę” i „Projekt 3D” („Mój Projekt 3D” albo kafel-odnośnik „Zrób Projekt 3D”). Wejście z zestawu asystenta
 * albo z raportu oferty zostaje jako dodatkowe źródło, zaznaczone z wejścia.
 * - „Wiem, czego chcę”: na jednym ekranie typ, moc, pojemność magazynu, opcjonalnie poszycie dachu i sprzęt
 *   (`SprzetKlienta`: producent, model albo kilka słów), od razu z końcówką. Bez sprzętu dobiera wykonawca, ze sprzętem
 *   pytamy, kto go kupi (`SPRZET_KONKRETNY`). Powód: nikt z reklamy nie odpowiadał na pierwsze pytanie „Kilka pytań”.
 * - „Kilka pytań”: prosty dobór osadzony w formularzu (`DoborZestawuProsty trybZlecenia`). `onGotowe(szkic)`
 *   (`lib/doborCele.szkicDoboru`) zwija go do „Twoje odpowiedzi” z „Zmień odpowiedzi”; dopiero wtedy pokazuje się
 *   końcówka. Sprzęt z odpowiedzi (`szkic.wybor`): `wykonawca` bez pytania o sprzęt, `zestaw` i `wlasny` jako kafelki
 *   (`SprzetZlecenia`) i dwie opcje `SPRZET_KONKRETNY` (kupuje wykonawca albo klient).
 * - Projekt 3D: moc, sprzęt i dach z projektu, zmiana tylko w Projekcie 3D; teczka idzie ze zleceniem, prosty audyt
 *   w oknie (G3, G6). Typ sprzeczny z projektem wymaga odpowiedzi na pytanie wprost (F9).
 *
 * Wspólna końcówka: województwo z mapy (D19) i miejscowość, budżet „około” z paskiem „Co zobaczą wykonawcy” (D15),
 * więcej informacji, 3, 6 albo 9 ofert i komentarz do zlecenia (do `MAKS_KOMENTARZA` znaków, widoczny od razu). Ulica, numer domu, telefon i zgoda w oknie po „Wystaw zlecenie” (D16); gość
 * zakłada tam konto (decyzja właściciela 2026-10-26).
 *
 * `przyklad`: tryb strony `/dla-domu`. Nic nie wysyła; po oknie adresu oddaje `onWyslane({ przyklad: true, ... })`.
 * `dobor`: zapisany wybór prostego doboru (odpowiedzi startowe kreatora). `zwykleCeny`: zwykłe ceny z przykładu.
 */

const ZRODLA = {
  zestaw: 'Z Twojego zestawu',
  sprawdz_oferte: 'Z oferty, którą sprawdzasz',
  odpowiedzi: 'Twoje odpowiedzi',
  projekt3d: 'Z Twojego projektu 3D',
  // Szkic z Projektu 3D przychodzi jako `zrodlo: 'wprost'` z `teczka_id` (`lib/teczka/dlaWykonawcy.ogloszenieZTeczki`).
  teczka: 'Z Twojej teczki instalacji',
}

const liczba = (v) => {
  const n = Number(String(v).replace(',', '.').replace(/\s/g, ''))
  return String(v).trim() && Number.isFinite(n) && n >= 0 ? n : null
}

/** Cele z prostego doboru (P4) słowami z makiety. */
const NAZWY_CELOW = {
  rachunek: 'Niższy rachunek', awaria: 'Prąd przy awarii', auto: 'Ładowanie auta', pompa: 'Pompa ciepła',
  rozbudowa_magazynu: 'Rozbudowa magazynu energii', estetyka: 'Estetyka', jakosc: 'Najwyższa jakość', dotacja: 'Dotacja',
  szybki_montaz: 'Szybki montaż',
}

const bezZnakow = (t) => String(t || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/ł/g, 'l').trim()
/** Klucz województwa z adresu Projektu 3D (nazwa „łódzkie” albo klucz „lodzkie”). */
export const kluczWojewodztwa = (w) => REGIONY.find((r) => r.key === w || bezZnakow(r.nazwa) === bezZnakow(w))?.key || ''

/**
 * Adres z Projektu 3D (`projekt.lokalizacja`): pola `adres` z geokodowania (`lib/projekt3d/klient3d.adresZWyniku`),
 * a gdy ulica albo numer są puste (punkt na mapie dalej niż 60 m od wyniku wyszukiwania), zapas z etykiety
 * wyszukanego adresu, np. „Słoneczna 12A, 95-100 Ozorków” (uwagi przeglądu D9). Null bez lokalizacji.
 */
export const adresZProjektu = (projekt) => {
  const lok = projekt?.lokalizacja
  if (!lok) return null
  const a = lok.adres || {}
  const [pierwsza = '', ...reszta] = String(lok.etykieta || '').split(',').map((x) => x.trim())
  const zEtykiety = pierwsza.match(/^(.*\D)\s+(\d+[A-Za-z]?(?:\/\d+[A-Za-z]?)?)$/)
  const miejscowoscZEtykiety = (reszta.find((x) => /\p{L}/u.test(x)) || '').replace(/^\d{2}-\d{3}\s+/, '').trim()
  return {
    wojewodztwo: kluczWojewodztwa(a.wojewodztwo || lok.wojewodztwo),
    miejscowosc: a.miejscowosc || miejscowoscZEtykiety,
    ulica: a.ulica || (zEtykiety ? zEtykiety[1].trim() : ''),
    nrDomu: a.nrDomu || (zEtykiety ? zEtykiety[2] : ''),
  }
}

/** Czy klient uzupełnił w teczce „Licznik i rozdzielnica” (część `miejsca`, pakiet P7). */
const maMiejsca = (miejsca) => {
  if (!miejsca) return false
  const m = normalizujMiejsca(miejsca)
  return Object.entries(m).some(([k, v]) => k !== 'wersja' && v && typeof v === 'object' && Object.values(v).some((x) => String(x).trim()))
}

const nazwaTypu = (k) => TYPY_OGLOSZENIA.find((t) => t.klucz === k)?.nazwa || k
const opcjaSprzetu = (k) => SPRZET_ZLECENIA.find((o) => o.klucz === k) || null

/** Źródła opisu instalacji: „Kilka pytań”, Projekt 3D i sprzęt z wejścia (zestaw asystenta albo raport oferty). */
const ZRODLA_OPISU = {
  pytania: { nazwa: 'Kilka pytań', opis: 'Rachunek, cele i dach. Resztę może zaproponować wykonawca.', ikona: 'lista' },
  wiem: { nazwa: 'Wiem, czego chcę', opis: 'Mam wstępny plan: moc, magazyn i budżet. Sprzęt mogę wskazać.', ikona: 'cel' },
  projekt: { nazwa: 'Mój Projekt 3D', opis: 'Moc, sprzęt i dach z projektu. Dołączymy teczkę instalacji.', ikona: 'projekt' },
  zestaw: { nazwa: 'Mój zestaw z asystenta', opis: 'Sprzęt z zestawu, który dobrał asystent.', ikona: 'zestaw' },
  sprawdz_oferte: { nazwa: 'Oferta, którą sprawdzam', opis: 'Sprzęt z oferty, którą już masz.', ikona: 'dokument' },
  wejscie: { nazwa: 'Wybrany sprzęt', opis: 'Sprzęt wybrany w poprzednim kroku.', ikona: 'zestaw' },
}
// Bez projektu kafel-odnośnik do Projektu 3D: „Wystaw zlecenie z tym projektem” wraca tu z projektem jako źródłem.
const ZROB_PROJEKT = { nazwa: 'Zrób Projekt 3D', adres: '/projekt-3d', opis: 'Ułóż moduły na swoim dachu. Wrócisz tu z projektem.' }

// Pytanie przy sprzeczności typu ze źródłem (F9): [ma magazyn, nie ma magazynu].
const MAGAZYN_W_ZRODLE = {
  projekt: ['Twój projekt ma magazyn energii.', 'Twój projekt nie ma magazynu energii.'],
  zestaw: ['Twój zestaw ma magazyn energii.', 'Twój zestaw nie ma magazynu energii.'],
  sprawdz_oferte: ['Oferta, którą sprawdzasz, ma magazyn energii.', 'Oferta, którą sprawdzasz, nie ma magazynu energii.'],
  wejscie: ['Wybrany sprzęt ma magazyn energii.', 'Wybrany sprzęt nie ma magazynu energii.'],
}
const Z_ZRODLA = {
  projekt: 'z Twojego projektu', zestaw: 'z Twojego zestawu', sprawdz_oferte: 'z oferty, którą sprawdzasz', wejscie: 'z wybranego sprzętu',
}
const MAGAZYNOWE = new Set(['pv', 'pv_magazyn'])
// „Wiem, czego chcę”: trzy typy, od których zależą pola mocy i pojemności (klucze jak `TYPY_OGLOSZENIA`).
// „Czego potrzebujesz?” w „Wiem, czego chcę”: te same części co przy rozbudowie (prośba właściciela 2026-10-30), typ
// zlecenia wynika z wyboru (`typPlanu`), a wybrany zakres idzie w opisie (`zakres`).
// Karty jak w „Co chcesz rozbudować?” kreatora (te same ikony), z opisami dla nowej instalacji.
const CZESCI_PLANU = [
  { klucz: 'moduly', nazwa: 'Moduły', opis: 'Na dach albo na grunt', ikona: faSolarPanel },
  { klucz: 'falownik', nazwa: 'Falownik', opis: 'Sieciowy albo hybrydowy', ikona: faPlug },
  { klucz: 'magazyn', nazwa: 'Magazyn energii', opis: 'Prąd na wieczór i na awarie', ikona: faBatteryFull },
]
export const typPlanu = (czesci) => {
  if (czesci.includes('moduly')) return czesci.includes('magazyn') ? 'pv_magazyn' : 'pv'
  if (czesci.includes('magazyn')) return 'magazyn'
  return czesci.length ? 'inne' : ''
}
// Konstrukcja z „Konkretny sprzęt” (te same poszycia i obrazki co w Projekcie 3D) do kategorii dachu, którą znają
// estymator (`zwykle_ceny`: Dachówka, Blacha, Papa, Grunt, Inny) i dopasowanie wykonawców.
const DACH_KONSTRUKCJI = {
  blacha_trapezowa: 'Blacha', blachodachowka: 'Blacha', rabek: 'Blacha', membrana_papa: 'Papa', balastowy_plaski: 'Papa',
  dachowka_ceramiczna: 'Dachówka', dachowka_karpiowka: 'Dachówka', dachowka_bitumiczna: 'Inny', grunt: 'Grunt',
}
export const dachZKonstrukcji = (klucz) => DACH_KONSTRUKCJI[klucz] || ''
/** Puste wartości poza opisem zlecenia (sprzęt klienta zwraca `null` dla niepodanych pól). */
const bezPustych = (obj) => Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== null && v !== '' && v !== undefined))
// Klucze opisu pokazywane w kafelkach sprzętu (zestaw albo sprzęt klienta); w wierszach podsumowania ich nie powtarzamy.
const KLUCZE_KAFLI = ['kwp', 'liczba_modulow', 'moduly', 'falownik', 'magazyn', 'magazyn_kwh']

const maOpis = (p) => Boolean((p?.opis && Object.keys(p.opis).length) || p?.podsumowanie?.length)

/**
 * Źródło, z którym przyszło wejście: `projekt` (szkic z Projektu 3D z `teczka_id`, w przykładzie `projekt3d`),
 * `dobor` (szkic „Kilka pytań” albo prostego doboru: `zrodlo: 'odpowiedzi'`, cele albo moc bez sprzętu), `wejscie`
 * (gotowy sprzęt z zestawu albo z oferty). Null: brak wejścia albo samo `typ` i `sprzet`.
 */
export const rodzajWejscia = (p) => {
  if (!p) return null
  if (p.teczka_id || p.zrodlo === 'projekt3d') return 'projekt'
  if (p.zrodlo === 'odpowiedzi') return 'dobor'
  if (maOpis(p)) return 'wejscie'
  if (p.cele?.length || p.kwp) return 'dobor'
  return null
}
const kluczWejscia = (p) => (['zestaw', 'sprawdz_oferte'].includes(p?.zrodlo) ? p.zrodlo : 'wejscie')

/** Wybór przy konkretnym sprzęcie z decyzji z wejścia: liczy się tylko, kto kupuje (F11). */
const konkretnyZWejscia = (p) => {
  const o = SPRZET_ZLECENIA.find((x) => x.klucz === sprzetZWejscia(p))
  if (!o) return ''
  return o.kto_kupuje === 'klient' ? 'konkretny_klient' : 'konkretny_wykonawca'
}

/**
 * Sprzęt z odpowiedzi „Kilka pytań” (`szkicDoboru.wybor`): `wykonawca`, `zestaw` albo `wlasny`. Szkic sprzed tej drogi
 * (np. przykład na `/dla-domu`) bez `wybor`: zestaw tylko wtedy, gdy opis ma sprzęt; samo „VoltValue dobiera” bez
 * zestawu zostawia sprzęt wykonawcy.
 */
export const wyborZeSzkicu = (s) => {
  if (!s) return null
  if (['wykonawca', 'zestaw', 'wlasny'].includes(s.wybor)) return s.wybor
  return s.opis?.moduly || s.opis?.falownik || s.opis?.magazyn ? 'zestaw' : 'wykonawca'
}

/** Magazyn w opisie źródła: pojemność albo nazwa (raport oferty potrafi przynieść „bez magazynu”). */
const maMagazynWOpisie = (o) => Boolean(Number(o?.magazyn_kwh) > 0 || (o?.magazyn && !/bez magazynu/i.test(String(o.magazyn))))

/** Dach do zlecenia (`DACHY_RYNKU`) z pokrycia w Projekcie 3D; null, gdy nie da się go przełożyć. */
export const dachZProjektu = (projekt) => {
  if (!projekt) return null
  if (projekt.rodzaj === 'grunt') return 'Grunt'
  const t = String(projekt.wyglad?.pokrycie?.typ || '')
  if (t.startsWith('dachowka_') && t !== 'dachowka_bitumiczna') return 'Dachówka'
  if (['blachodachowka', 'rabek', 'blacha_trapezowa'].includes(t)) return 'Blacha'
  if (t === 'membrana') return 'Papa'
  return null
}

const Blad = ({ children }) => (
  <p className="blad" role="alert"><Ikona nazwa="uwaga" rozmiar={16} grubosc={2.2} />{children}</p>
)

/** Wiersz podsumowania; „Zmień” otwiera wybór w tym samym miejscu. */
const Wiersz = ({ etykieta, wartosc, onZmien, przycisk = 'Zmień' }) => (
  <div className="wiersz">
    <dt>{etykieta}</dt>
    <dd>
      <span>{wartosc}</span>
      {onZmien && <button type="button" className="zmien" onClick={onZmien} aria-label={`${przycisk}: ${etykieta}`}>{przycisk}</button>}
    </dd>
  </div>
)

// Wiersze „Twoje odpowiedzi”, które da się poprawić wprost (etykiety z `lib/rynek.POLA_OPISU`).
const EDYTOWALNE = {
  Moc: { klucz: 'kwp', jednostka: 'kWp', krokPola: 0.1, maks: 50 },
  'Pojemność magazynu': { klucz: 'magazyn_kwh', jednostka: 'kWh', krokPola: 0.1, maks: 100 },
}

/** Wiersz z liczbą do poprawienia na miejscu: „Zmień” zamienia wartość w pole z jednostką, Enter zapisuje. */
const WierszLiczby = ({ etykieta, wartosc, jednostka, krokPola, maks, onZapisz }) => {
  const [edycja, setEdycja] = useState(null)
  const id = useId()
  const n = edycja === null ? null : Number(String(edycja).replace(',', '.'))
  const poprawna = n !== null && Number.isFinite(n) && n > 0 && n <= maks
  const zapisz = () => { if (!poprawna) return; onZapisz(Math.round(n * 100) / 100); setEdycja(null) }
  if (edycja === null) {
    return <Wiersz etykieta={etykieta} wartosc={`${String(wartosc).replace('.', ',')} ${jednostka}`}
                   onZmien={() => setEdycja(String(wartosc).replace('.', ','))} />
  }
  return (
    <div className="wiersz">
      <dt><label htmlFor={id}>{etykieta}</label></dt>
      <dd className="wiersz-edycja">
        <span className="pole-z-jednostka">
          <input id={id} className="wejscie" inputMode="decimal" autoFocus value={edycja} aria-invalid={!poprawna}
                 onChange={(e) => setEdycja(e.target.value)}
                 onKeyDown={(e) => {
                   if (e.key === 'Enter') { e.preventDefault(); zapisz() }
                   if (e.key === 'Escape') setEdycja(null)
                 }} />
          <span aria-hidden="true">{jednostka}</span>
        </span>
        <button type="button" className="zmien" onClick={zapisz} disabled={!poprawna}>Zapisz</button>
        <button type="button" className="zmien" onClick={() => setEdycja(null)}>Anuluj</button>
      </dd>
    </div>
  )
}

const Chip = ({ on, onClick, children }) => (
  <button type="button" className="chip-wybor" aria-pressed={on} onClick={onClick}>
    {on && <Ikona nazwa="ok" rozmiar={13} grubosc={3} />}{children}
  </button>
)

/** Kto kupuje konkretny sprzęt (projekt, zestaw, sprzęt klienta, oferta): wykonawca albo klient (F11). */
const WyborSprzetuZlecenia = ({ wartosc, onZmiana, blad }) => {
  const id = useId()
  const opcja = SPRZET_KONKRETNY.find((o) => o.klucz === wartosc) || null
  return (
    <WyborKafla nazwa={`${id}-sprzet`} className="opcje-sprzetu dwie" etykieta="Kto zajmie się sprzętem" blad={blad}
                wartosc={opcja ? wartosc : ''} onZmiana={onZmiana}
                opcje={SPRZET_KONKRETNY.map((o) => ({
                  wartosc: o.klucz,
                  tresc: <><span className="kafel-krotko">{o.krotko}</span><strong>{o.nazwa}</strong><span className="kafel-opis">{o.opis}</span></>,
                }))} />
  )
}

/**
 * Pytanie przy sprzeczności typu ze źródłem (F9): „Twój projekt ma magazyn energii. Wystawić zlecenie na fotowoltaikę
 * z magazynem czy bez?”. Przy innym typie niż fotowoltaika z magazynem albo bez: zostać przy źródle albo „Kilka pytań”.
 */
const PytanieOTyp = ({ zrodlo, typZrodla, nowy, maMagazyn, onMagazyn, onZostaw, onPytania }) => {
  const oMagazyn = MAGAZYNOWE.has(nowy) && MAGAZYNOWE.has(typZrodla)
  return (
    <div role="group" aria-label="Typ zlecenia a źródło" aria-live="polite"
         className="mt-3 flex gap-2.5 rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-3">
      <span className="mt-0.5 flex-shrink-0 text-amber-700"><ExclamationTriangleIcon className="h-5 w-5" aria-hidden="true" /></span>
      <div className="min-w-0 text-[13.5px] leading-snug">
        <p className="font-semibold text-gray-900">
          {oMagazyn
            ? `${MAGAZYN_W_ZRODLE[zrodlo][maMagazyn ? 0 : 1]} Wystawić zlecenie na fotowoltaikę z magazynem czy bez?`
            : `„${nazwaTypu(nowy)}” nie wynika ${Z_ZRODLA[zrodlo]}. Wystawić zlecenie według źródła czy odpowiedzieć na kilka pytań?`}
        </p>
        <div className="mt-2 flex flex-wrap gap-2">
          {oMagazyn ? (
            <>
              <button type="button" className="btn btn-obrys btn-m" onClick={() => onMagazyn(true)}>Z magazynem</button>
              <button type="button" className="btn btn-obrys btn-m" onClick={() => onMagazyn(false)}>Bez magazynu</button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-obrys btn-m" onClick={onZostaw}>Zostaw „{nazwaTypu(typZrodla)}”</button>
              <button type="button" className="btn btn-obrys btn-m" onClick={onPytania}>Kilka pytań</button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const FormularzOgloszenia = ({ poczatkowe = null, dobor = null, onWyslane, przyklad = false, zwykleCeny: zwykleZPrzykladu = null }) => {
  const id = useId()
  const formularz = useRef(null)
  const podsumowanieRef = useRef(null)
  const wejscie = rodzajWejscia(poczatkowe)
  // Domyślnie „Kilka pytań”; wejście z Projektu 3D albo z zestawu czy oferty zaznacza swoje źródło (H5).
  const [zrodlo, setZrodlo] = useState(wejscie === 'projekt' ? 'projekt' : wejscie === 'wejscie' ? 'wejscie' : 'pytania')
  // Szkic „Kilka pytań” (`lib/doborCele.szkicDoboru`): z wejścia (np. „Wystaw zlecenie” na `/dobierz-zestaw`) albo z kreatora.
  const [szkic, setSzkic] = useState(wejscie === 'dobor' ? poczatkowe : null)
  const [edytujPytania, setEdytujPytania] = useState(false)
  // Moc i pojemność magazynu poprawione wprost w „Twoje odpowiedzi” (prośba właściciela 2026-10-30), bez ponownego kreatora.
  const [poprawki, setPoprawki] = useState({})
  // „Wiem, czego chcę” (2026-10-29): typ, moc, pojemność i opcjonalny sprzęt klienta.
  const [planCzesci, setPlanCzesci] = useState(['moduly', 'falownik'])
  const planTyp = typPlanu(planCzesci)
  const planModuly = planCzesci.includes('moduly')
  const planMagazyn = planCzesci.includes('magazyn')
  const [planKonstrukcja, setPlanKonstrukcja] = useState('')
  const [pokazKonstrukcje, setPokazKonstrukcje] = useState(false)
  const [planKwp, setPlanKwp] = useState('')
  const [planKwh, setPlanKwh] = useState('')
  const [planSprzet, setPlanSprzet] = useState(PUSTY_SPRZET)
  const [pokazPlanSprzet, setPokazPlanSprzet] = useState(false)
  // Typ inny niż wynika ze źródła (po pytaniu wprost) i „Bez magazynu” przy źródle z magazynem (F9).
  const [typNadpisany, setTypNadpisany] = useState(null)
  const [bezMagazynu, setBezMagazynu] = useState(false)
  const [konflikt, setKonflikt] = useState(null)
  const [zuzycie, setZuzycie] = useState(poczatkowe?.zuzycie_kwh ? String(poczatkowe.zuzycie_kwh) : '')
  const [dach, setDach] = useState(poczatkowe?.opis?.dach || poczatkowe?.dach || '')
  const [budynek, setBudynek] = useState(poczatkowe?.opis?.budynek || '')
  const [budzet, setBudzet] = useState(formatPola(poczatkowe?.budzet || poczatkowe?.budzet_kwota || 0))
  const [budzetRecznie, setBudzetRecznie] = useState(false)
  const [budzetZOdpowiedzi, setBudzetZOdpowiedzi] = useState(Boolean(poczatkowe?.budzet || (wejscie === 'dobor' && poczatkowe?.budzet_kwota)))
  const [wojewodztwo, setWojewodztwo] = useState(poczatkowe?.wojewodztwo || '')
  const [miejscowosc, setMiejscowosc] = useState(poczatkowe?.miejscowosc || '')
  const [termin, setTermin] = useState(poczatkowe?.termin || 'do_1_mies')
  const [pme2, setPme2] = useState(Boolean(poczatkowe?.pme2))
  const [komentarz, setKomentarz] = useState('')
  const [pokazCene, setPokazCene] = useState(false)
  const [liczbaFirm, setLiczbaFirm] = useState(null)
  // Kto kupuje konkretny sprzęt (F11); z wejścia z tej samej decyzji co dawne `sprzet`.
  const [sprzetKonkretny, setSprzetKonkretny] = useState(() => konkretnyZWejscia(poczatkowe))
  // Rzeczy przyniesione z drogi są podsumowaniem; „Zmień” otwiera ich wybór (D7).
  const [zmienTyp, setZmienTyp] = useState(false)
  const [zmienSprzet, setZmienSprzet] = useState(() => !sprzetZWejscia(poczatkowe))
  const [wiecej, setWiecej] = useState(false)
  const [podglad, setPodglad] = useState(false)
  const zamknijPodglad = useCallback(() => setPodglad(false), [])
  const [proba, setProba] = useState(false)
  // Okno adresu i kontaktu (D16).
  const [okno, setOkno] = useState(false)
  const [ulica, setUlica] = useState('')
  const [nrDomu, setNrDomu] = useState('')
  const [telefon, setTelefon] = useState('')
  const [zgoda, setZgoda] = useState(false)
  const [probaOkna, setProbaOkna] = useState(false)
  // Gość zakłada konto w oknie „Wystaw zlecenie” albo loguje się w nim (decyzja właściciela 2026-10-26).
  const { isAuthenticated, user } = useAuth()
  const gosc = !przyklad && !isAuthenticated
  // Pomiar ścieżki /rynek (2026-10-28): kroki i braki bez treści pól; przykład na `/dla-domu` nic nie wysyła.
  const krok = (cel) => { if (!przyklad) zdarzenie('rynek_krok', cel) }
  const zgloszBraki = (b) => { if (!przyklad) Object.keys(b).filter((k) => b[k]).forEach((k) => zdarzenie('rynek_blad', k)) }
  const [konto, setKonto] = useState(PUSTE_KONTO)
  const [trybKonta, setTrybKonta] = useState('nowe')
  const [bledyKontaZSerwera, setBledyKontaZSerwera] = useState({})
  const [komunikatKonta, setKomunikatKonta] = useState('')
  const [busy, setBusy] = useState(false)
  const [blad, setBlad] = useState('')
  const [zwykleZApi, setZwykleZApi] = useState(null)
  // Teczka instalacji z Projektu 3D (pakiet P5): idzie ze zleceniem, gdy źródłem jest Projekt 3D.
  const { teczka, ustalanie: teczkaUstalana, zapis: zapisTeczki, zapiszCzesc } = useTeczka()
  const [oknoMiejsc, setOknoMiejsc] = useState(false)
  const zamknijMiejsca = useCallback(() => setOknoMiejsc(false), [])
  const [adresZ3D, setAdresZ3D] = useState(false)
  // Projekt z teczki konta, także zanim klient wybierze go jako źródło (uwagi przeglądu D8).
  const projektKonta = !przyklad ? teczka?.projekt || null : null
  const szkicTeczki = useMemo(() => (projektKonta ? ogloszenieZTeczki(teczka) : null), [projektKonta, teczka])
  const szkicProjektu = szkicTeczki || (wejscie === 'projekt' ? poczatkowe : null)

  // Adres z Projektu 3D (uwagi przeglądu C4, C7, D8, D9): województwo i miejscowość od razu w formularzu, ulica i numer
  // w oknie adresu. Tylko puste pola, nic, co klient już wpisał.
  useEffect(() => {
    const a = adresZProjektu(projektKonta)
    if (!a) return
    if (a.wojewodztwo) setWojewodztwo((w) => w || a.wojewodztwo)
    if (a.miejscowosc) setMiejscowosc((m) => m || a.miejscowosc)
    if (a.ulica) setUlica((u) => u || a.ulica)
    if (a.nrDomu) setNrDomu((n) => n || a.nrDomu)
    if (a.ulica || a.nrDomu) setAdresZ3D(true)
  }, [projektKonta])

  // Trzy drogi (decyzje 2026-10-26 i 2026-10-29): „Kilka pytań”, „Wiem, czego chcę” i Projekt 3D; sprzęt z wejścia na
  // początku, gdy klient z nim przyszedł.
  const zrodla = [
    ...(wejscie === 'wejscie' ? ['wejscie'] : []),
    'pytania',
    'wiem',
    ...(szkicProjektu ? ['projekt'] : []),
  ]
  const zrobProjekt = !przyklad && !szkicProjektu && !teczkaUstalana
  // Przykład na `/dla-domu` pokazuje jedną drogę: kreatora tam nie uruchamiamy.
  const pokazPytanie = !przyklad && zrodla.length + (zrobProjekt ? 1 : 0) > 1
  const klucz = zrodlo === 'wejscie' ? kluczWejscia(poczatkowe) : zrodlo
  const wyborPytan = zrodlo === 'pytania' ? wyborZeSzkicu(szkic) : null
  const planZeSprzetem = zrodlo === 'wiem' && maOpisSprzetu(planSprzet)
  const konkretny = zrodlo === 'projekt' || zrodlo === 'wejscie' || ['zestaw', 'wlasny'].includes(wyborPytan) || planZeSprzetem
  // Końcówka (gdzie, budżet, oferty) dopiero przy gotowym opisie: przy „Kilka pytań” po „Dalej” w kreatorze.
  const gotowyOpis = zrodlo !== 'pytania' || Boolean(szkic && !edytujPytania)
  const kafleSprzetu = ['zestaw', 'wlasny'].includes(wyborPytan)

  // Opis instalacji ze źródła; dach z projektu, a bez niego z wyboru klienta w „Więcej informacji”.
  const dachProjektu = zrodlo === 'projekt' ? dachZProjektu(projektKonta) : null
  const opisZrodla = (() => {
    if (zrodlo === 'projekt') return { ...(szkicProjektu?.opis || {}), ...(dachProjektu ? { dach: dachProjektu } : {}) }
    if (zrodlo === 'wejscie') return { ...(poczatkowe.opis || {}) }
    if (zrodlo === 'wiem') {
      // Moc i pojemność wpisane wprost mają pierwszeństwo przed wyliczonymi ze sprzętu; dach dokłada `o` z wyboru niżej.
      const sprzet = planZeSprzetem ? bezPustych(opisSprzetuKlienta(planSprzet)) : {}
      const { kwp: kwpSprzetu, magazyn_kwh: kwhSprzetu, ...reszta } = sprzet
      return {
        ...reszta,
        zakres: CZESCI_PLANU.filter((c) => planCzesci.includes(c.klucz)).map((c) => c.nazwa).join(', ') || null,
        kwp: planModuly ? liczba(planKwp) || kwpSprzetu || null : null,
        magazyn_kwh: planMagazyn ? liczba(planKwh) || kwhSprzetu || null : null,
        konstrukcja: planModuly && planKonstrukcja ? KONSTRUKCJE.find((k) => k.key === planKonstrukcja)?.label || null : null,
      }
    }
    if (!szkic) return {}
    // Szkic „Kilka pytań” niesie opis z mocą, magazynem, dachem, rozbudową i sprzętem; starszy szkic doboru te pola wprost.
    return {
      kwp: szkic.kwp || null, magazyn_kwh: szkic.magazyn_kwh || null, dach: szkic.dach || null,
      rozbudowa: szkic.rozbudowa || null, obecna_instalacja: szkic.obecna_instalacja || null,
      ...(szkic.opis || {}), cele: szkic.cele?.length ? szkic.cele : null,
    }
  })()
  const zrodloMaMagazyn = maMagazynWOpisie(opisZrodla)
  const typZrodla = (() => {
    if (zrodlo === 'projekt') return zrodloMaMagazyn ? 'pv_magazyn' : 'pv'
    if (zrodlo === 'wejscie') return poczatkowe.typ || (zrodloMaMagazyn ? 'pv_magazyn' : 'pv')
    if (zrodlo === 'wiem') return planTyp
    if (!szkic) return ''
    return szkic.typ || (szkic.cele?.includes('rozbudowa_magazynu') ? 'magazyn' : zrodloMaMagazyn ? 'pv_magazyn' : 'pv')
  })()
  // Przy „Kilka pytań” typ wynika z odpowiedzi (zmiana przez „Zmień odpowiedzi”), przy projekcie i wejściu da się go zmienić.
  // Przy „Wiem, czego chcę” typ wybiera się wprost w planie.
  const typTeraz = zrodlo === 'pytania' || zrodlo === 'wiem' ? typZrodla : typNadpisany || typZrodla

  // „Wykonawca zaproponuje” z odpowiedzi: sprzęt po stronie wykonawcy, bez pytania o sprzęt.
  const opcja = konkretny ? SPRZET_KONKRETNY.find((x) => x.klucz === sprzetKonkretny) || null : opcjaSprzetu('wykonawca')
  const cenaOferty = poczatkowe?.cena_oferty || null
  const teczkaId = zrodlo === 'projekt' && !przyklad ? szkicProjektu?.teczka_id ?? null : null
  const teczkaZlecenia = teczkaId && teczka?.id === teczkaId ? teczka : null

  const wybierzZrodlo = (k) => {
    krok(`zrodlo:${k}`)
    setZrodlo(k)
    setTypNadpisany(null)
    setBezMagazynu(false)
    setKonflikt(null)
    setZmienTyp(false)
    if (k === 'pytania' && szkic) {
      const s = konkretnyZWejscia(szkic)
      setSprzetKonkretny(s)
      setZmienSprzet(!s)
    }
  }

  // Koniec „Kilka pytań”: kreator zwija się do podsumowania, a odpowiedzi wypełniają budżet, zużycie, termin i sprzęt.
  const gotowePytania = (s) => {
    setSzkic(s)
    setEdytujPytania(false)
    setPoprawki({})
    if (s.budzet_kwota && !budzetRecznie) { setBudzet(formatPola(s.budzet_kwota)); setBudzetZOdpowiedzi(true) }
    if (s.zuzycie_kwh) setZuzycie(String(s.zuzycie_kwh))
    if (s.termin) setTermin(s.termin)
    if (s.pme2) setPme2(true)
    const k = konkretnyZWejscia(s)
    setSprzetKonkretny(k)
    setZmienSprzet(!k)
    zdarzenie('rynek_kilka_pytan', wyborZeSzkicu(s))
    setTimeout(() => podsumowanieRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' }), 0)
  }

  // Zmiana typu przy projekcie albo sprzęcie z wejścia: zgodny od razu, sprzeczny przez pytanie wprost (F9).
  const wybierzTyp = (t) => {
    if (t === typTeraz) { setKonflikt(null); return }
    if (t === typZrodla && !bezMagazynu) { setTypNadpisany(null); setKonflikt(null); setZmienTyp(false); return }
    setKonflikt(t)
  }
  const rozstrzygnijMagazyn = (zMagazynem) => {
    const t = zMagazynem ? 'pv_magazyn' : 'pv'
    setTypNadpisany(t === typZrodla ? null : t)
    setBezMagazynu(!zMagazynem && zrodloMaMagazyn)
    setKonflikt(null)
    setZmienTyp(false)
  }

  const o = (() => {
    // Przy samym magazynie z drogi „Wiem, czego chcę” dach nie ma znaczenia, więc go nie wysyłamy.
    const bezDachu = zrodlo === 'wiem' && !planModuly
    const w = { ...opisZrodla, ...(dach && !opisZrodla.dach && !bezDachu ? { dach } : {}), ...(budynek ? { budynek } : {}) }
    if (bezMagazynu) { delete w.magazyn; delete w.magazyn_kwh; delete w.magazyn_marka }
    if (zrodlo === 'pytania') {
      if (poprawki.kwp && w.kwp) w.kwp = poprawki.kwp
      if (poprawki.magazyn_kwh && w.magazyn_kwh) w.magazyn_kwh = poprawki.magazyn_kwh
    }
    return w
  })()

  // Zwykłe ceny z estymatora (P4): przy każdej zmianie mocy, sprzętu, dachu albo województwa, z krótką zwłoką.
  // F10: z tego, co klient opisał (moc i magazyn ze źródła), bez magazynu przy fotowoltaice. Sam falownik z „Wiem, czego
  // chcę” (typ „inne”) nie ma szacunku: estymator liczy całe instalacje.
  const kluczZapytania = przyklad || !gotowyOpis || (zrodlo === 'wiem' && planTyp === 'inne') ? null : JSON.stringify({
    kwp: o.kwp || null, zuzycie_kwh: liczba(zuzycie),
    magazyn_kwh: o.magazyn_kwh ?? (typTeraz === 'pv' ? 0 : null),
    kto_kupuje: opcja?.kto_kupuje, dach: o.dach, zestaw_id: poczatkowe?.zestaw_id, wojewodztwo,
  })
  useEffect(() => {
    if (!kluczZapytania) return undefined
    let anulowane = false
    const t = setTimeout(() => {
      const p = JSON.parse(kluczZapytania)
      if (p.dach) {
        pobierzZwykleCeny(p).then((w) => { if (!anulowane) setZwykleZApi(w) })
        return
      }
      // Rodzaju dachu nie wymagamy (właściciel 2026-10-29): dolna granica jak na blasze, górna jak na gruncie.
      Promise.all([pobierzZwykleCeny({ ...p, dach: 'Blacha' }), pobierzZwykleCeny({ ...p, dach: 'Grunt' })]).then(([nisko, wysoko]) => {
        if (anulowane) return
        if (!nisko || !wysoko) { setZwykleZApi(nisko || wysoko); return }
        setZwykleZApi({
          ...nisko,
          od: Math.min(nisko.od, wysoko.od), do: Math.max(nisko.do, wysoko.do),
          montaz_od: Math.min(nisko.montaz_od, wysoko.montaz_od), montaz_do: Math.max(nisko.montaz_do, wysoko.montaz_do),
        })
      })
    }, 400)
    return () => { anulowane = true; clearTimeout(t) }
  }, [kluczZapytania])

  // Przykład podaje ceny montażu przy zestawie zamawianym samodzielnie; przy innej opcji cały zestaw idzie do wykonawcy.
  const zwykle = (() => {
    if (przyklad && zwykleZPrzykladu) {
      const { od, do: gora, zestaw_cena: zestaw } = zwykleZPrzykladu
      if (!zestaw) return { od, do: gora }
      return opcja?.kto_kupuje === 'klient' ? { od, do: gora, zestaw_cena: zestaw } : { od: od + zestaw, do: gora + zestaw }
    }
    if (!zwykleZApi) return null
    // Przy zestawie zamawianym przez klienta estymator podaje sam montaż: bez ceny zestawu to tylko informacja (F10).
    const klient = opcja?.kto_kupuje === 'klient'
    return { od: zwykleZApi.od, do: zwykleZApi.do, zestaw_cena: klient ? zwykleZApi.zestaw_cena : null, tylkoMontaz: klient,
             zrodlo: zwykleZApi.zrodlo || null, data_cen: zwykleZApi.data_cen || null }
  })()
  // Co wiemy o instalacji na pewno (blok „Ile może kosztować”, 2026-10-29): przy brakach szacunek jest wstępny.
  // Moc z rachunku i dobrany magazyn bierzemy z odpowiedzi estymatora (`zrodlo`), oznaczone jako przybliżone.
  // W przykładzie na `/dla-domu` podsumowanie jest wpisane na sztywno, więc nic tu nie wiemy.
  // „Kilka pytań” zawsze liczy moc z rachunku albo zużycia i dobiera magazyn, więc te wartości są przybliżone; pewne są
  // z Projektu 3D i z konkretnego sprzętu.
  const zrodloCen = zwykleZApi?.zrodlo || {}
  const zPytan = zrodlo === 'pytania'
  const magazynCeny = typTeraz === 'pv' ? 0 : (o.magazyn_kwh ?? zrodloCen.magazyn_kwh ?? null)
  const podstawaCeny = przyklad ? null : {
    kwp: o.kwp || zrodloCen.kwp || null,
    // Wartość poprawiona przez klienta w „Twoje odpowiedzi” jest podana, nie dobrana.
    kwpZRachunku: (zPytan && !poprawki.kwp) || (!o.kwp && Boolean(zrodloCen.kwp)),
    magazyn_kwh: magazynCeny,
    magazynDobrany: typTeraz !== 'pv' && Boolean(magazynCeny) && ((zPytan && !poprawki.magazyn_kwh) || o.magazyn_kwh == null),
    dach: o.dach || null,
    // Estymator liczy z ceny zestawu tylko przy zapisanym zestawie (`zestaw_id`); modeli z Projektu 3D jeszcze nie bierze.
    sprzet: Boolean(poczatkowe?.zestaw_id),
  }

  // „Wiem, czego chcę”: moc przy fotowoltaice i pojemność przy magazynie są wymagane.
  const brakCzesci = zrodlo === 'wiem' && !planCzesci.length
  const brakMocy = zrodlo === 'wiem' && planModuly && !(liczba(planKwp) > 0)
  const brakPojemnosci = zrodlo === 'wiem' && planMagazyn && !(liczba(planKwh) > 0)
  const braki = {
    pytania: !gotowyOpis,
    plan: brakCzesci || brakMocy || brakPojemnosci,
    typ: gotowyOpis && !typTeraz,
    sprzet: gotowyOpis && !opcja,
    gdzie: !wojewodztwo || !miejscowosc.trim(),
    liczba: !liczbaFirm,
  }
  const brakiOkna = { adres: !ulica.trim() || !nrDomu.trim(), zgoda: !zgoda }
  const brakiKontaGoscia = gosc && trybKonta === 'nowe' ? brakiKonta(konto) : {}
  const bledyKonta = { ...(probaOkna ? brakiKontaGoscia : {}), ...bledyKontaZSerwera }

  const wystaw = (e) => {
    e.preventDefault()
    setProba(true)
    krok('wystaw')
    if (braki.sprzet) setZmienSprzet(true)
    if (Object.values(braki).some(Boolean)) {
      zgloszBraki(braki)
      // Pierwszy brak na ekran, także gdy jest wyżej niż przycisk.
      setTimeout(() => formularz.current?.querySelector('[role="alert"]')?.scrollIntoView?.({ block: 'center', behavior: 'smooth' }), 0)
      return
    }
    setBlad('')
    setProbaOkna(false)
    setOkno(true)
    krok('okno')
  }
  const zamknijOkno = () => { krok('okno_zamkniete'); setOkno(false) }

  const wyslij = async (e) => {
    e.preventDefault()
    setProbaOkna(true)
    setBlad('')
    krok('wyslij')
    if (Object.values(brakiOkna).some(Boolean)) { zgloszBraki(brakiOkna); return }
    if (przyklad) {
      setOkno(false)
      onWyslane?.({ przyklad: true, liczba_firm: liczbaFirm })
      return
    }
    // Gość w trybie logowania wysyła dopiero po zalogowaniu (przycisk „Zaloguj się” w oknie).
    if (gosc && trybKonta === 'logowanie') { zgloszBraki({ logowanie: true }); setBlad('Zaloguj się albo załóż konto, żeby wystawić zlecenie.'); return }
    if (gosc && Object.keys(brakiKontaGoscia).length) { zgloszBraki(brakiKontaGoscia); return }
    await wyslijTeraz(gosc)
  }

  /** Pole konta gościa z błędu backendu (`400 {detail, kod, pole}`); inne błędy idą pod formularz. */
  const POLA_KONTA = { email: 'email', haslo: 'haslo', haslo2: 'haslo2', zgoda_regulamin: 'regulamin' }
  const poleKonta = (dane) => POLA_KONTA[dane?.pole] || null

  const wyslijTeraz = async (zKontem) => {
    setBusy(true)
    setBledyKontaZSerwera({})
    const kwota = kwotaZPola(budzet) || null
    const dane = {
      typ: typTeraz,
      tryb: zrodlo === 'pytania' ? szkic?.tryb || (konkretny ? 'szczegolowe' : 'proste')
        : zrodlo === 'wiem' && !konkretny ? 'proste' : 'szczegolowe',
      zrodlo: zrodlo === 'wejscie' && ['zestaw', 'sprawdz_oferte'].includes(poczatkowe?.zrodlo) ? poczatkowe.zrodlo : 'wprost',
      wojewodztwo,
      powiat: '',
      miejscowosc: miejscowosc.trim(),
      opis: Object.fromEntries(Object.entries(o).filter(([, v]) => v !== null && v !== '' && v !== undefined)),
      zuzycie_kwh: liczba(zuzycie) != null ? Math.round(liczba(zuzycie)) : null,
      termin,
      pme2,
      komentarz: komentarz.trim(),
      cena_oferty: cenaOferty,
      pokaz_cene_oferty: Boolean(cenaOferty) && pokazCene,
      telefon: telefon.trim(),
      zgoda,
      tresc_zgody: TRESC_ZGODY.zlecenie,
      tryb_kontaktu: TRYB_PRZETARGU,
      liczba_firm: liczbaFirm,
      // Kontrakt zlecenia (sekcja 4 planu): zakres dla wykonawców liczy backend przy wystawieniu.
      kto_dobiera: opcja.kto_dobiera,
      kto_kupuje: opcja.kto_kupuje,
      budzet_kwota: kwota,
      ulica: ulica.trim(),
      nr_domu: nrDomu.trim(),
      teczka_id: teczkaId,
      // Backend sprzed pakietu P0 zna tylko te dwa pola; do usunięcia po scaleniu P0.
      sprzet: opcja.sprzet,
      budzet_brutto: kwota,
    }
    // Gość nie ma teczki (projekt wymaga konta), backend odrzuca `teczka_id` w zleceniu z kontem.
    const { teczka_id: _bezTeczki, ...daneGoscia } = dane
    const res = zKontem
      ? await api.dodajOgloszenieZKontem({ ...daneGoscia, email: konto.email.trim(), haslo: konto.haslo, haslo2: konto.haslo2,
        zgoda_regulamin: konto.regulamin, zgoda_marketing: konto.marketing })
      : await api.dodajOgloszenie(dane)
    setBusy(false)
    if (!res.ok && zKontem && res.httpStatus === 409 && res.data?.kod === 'konto_istnieje') {
      // Zlecenie zostaje w oknie: po zalogowaniu wyślemy je z tego konta.
      zgloszBraki({ konto_istnieje: true })
      setTrybKonta('logowanie')
      setKomunikatKonta('Masz już konto z tym adresem. Zaloguj się, a zlecenie wyślemy z Twojego konta.')
      return
    }
    if (!res.ok && zKontem && poleKonta(res.data)) {
      zgloszBraki({ [poleKonta(res.data)]: true })
      setBledyKontaZSerwera({ [poleKonta(res.data)]: res.error })
      return
    }
    if (!res.ok) { zgloszBraki({ serwer: true }); setBlad(res.error); return }
    krok('wyslane')
    if (zKontem) {
      zdarzenie('rynek_zlecenie_gosc', `${opcja.klucz}:${liczbaFirm}`)
      setOkno(false)
      onWyslane?.({ stan: 'czeka_na_email', email: res.data?.email || konto.email.trim() })
      return
    }
    // Konto z niepotwierdzonym adresem: zlecenie czeka tak samo jak u gościa (backend `rynek/gosc.py`).
    if (res.data?.status === 'czeka_na_email' || res.data?.stan === 'czeka_na_email') {
      setOkno(false)
      onWyslane?.({ stan: 'czeka_na_email', email: res.data?.email || user?.email || '' })
      return
    }
    zdarzenie('rynek_zlecenie', `${opcja.klucz}:${liczbaFirm}`)
    zdarzenie('rynek_zrodlo_opisu', zrodlo)
    if (teczkaId) zdarzenie('teczka_do_zlecenia', poczatkowe?.teczka_id ? 'projekt3d' : 'formularz')
    setOkno(false)
    onWyslane?.(res.data)
  }


  // Dach do wyboru tylko wtedy, gdy nie wynika ze źródła.
  const wyborDachu = !opisZrodla?.dach && (
    <fieldset className="grupa">
      <legend>Dach <small>(opcjonalnie)</small></legend>
      <div className="chipy-wybor">
        {DACHY_RYNKU.map((d) => <Chip key={d} on={dach === d} onClick={() => setDach(dach === d ? '' : d)}>{d}</Chip>)}
      </div>
    </fieldset>
  )

  // W „Wiem, czego chcę” dach jest w planie, więc w „Więcej informacji” go nie powtarzamy.
  const dachWWiecej = zrodlo !== 'wiem' && wyborDachu

  const zwiersze = (dane) => [
    ...(dane?.cele?.length ? [['Cele', dane.cele.map((c) => NAZWY_CELOW[c] || c).join(', ')]] : []),
    ...(dane?.zuzycie_kwh ? [['Roczne zużycie', `${Number(dane.zuzycie_kwh).toLocaleString('pl-PL')} kWh`]] : []),
  ]
  // Wiersze bez celów i bez tego, co pokazują kafelki sprzętu (zestaw albo sprzęt klienta).
  const opisDoWierszy = Object.fromEntries(Object.entries(o)
    .filter(([k]) => k !== 'cele' && !(kafleSprzetu && [...KLUCZE_KAFLI, 'dach'].includes(k))))
  const podsumowanie = (() => {
    if (zrodlo === (wejscie === 'dobor' ? 'pytania' : wejscie) && szkic === poczatkowe && poczatkowe?.podsumowanie?.length) {
      return poczatkowe.podsumowanie
    }
    if (zrodlo === 'wejscie' && poczatkowe?.podsumowanie?.length) return poczatkowe.podsumowanie
    if (zrodlo === 'pytania') return [...zwiersze(szkic), ...wierszeOpisu(opisDoWierszy)]
    if (zrodlo === 'wejscie') return [...zwiersze(poczatkowe), ...wierszeOpisu(opisDoWierszy)]
    return wierszeOpisu(opisDoWierszy)
  })()
  const naglowekZrodla = zrodlo === 'projekt'
    ? (szkicProjektu?.teczka_id ? ZRODLA.teczka : ZRODLA.projekt3d)
    : zrodlo === 'pytania' ? ZRODLA.odpowiedzi : ZRODLA[poczatkowe?.zrodlo] || 'Twoje zlecenie'
  const pokazSprzet = gotowyOpis && konkretny && (zmienSprzet || !opcja)
  const ustawSprzet = (k) => { setSprzetKonkretny(k); setZmienSprzet(true) }

  return (
    <>
      <form ref={formularz} className="karta-tresc" onSubmit={wystaw} noValidate>
        {/* Dwie drogi (decyzja 2026-10-26): „Kilka pytań” i Projekt 3D, bez projektu kafel-odnośnik „Zrób Projekt 3D”. */}
        {pokazPytanie && (
          <fieldset className="grupa">
            <legend>Na podstawie czego wystawiamy zlecenie?</legend>
            <div className="opcje-zrodla">
              <WyborKafla nazwa={`${id}-zrodlo`} className="kafle-zrodel" etykieta="Na podstawie czego wystawiamy zlecenie"
                          wartosc={zrodlo} onZmiana={wybierzZrodlo}
                          opcje={zrodla.map((k) => {
                            const z = ZRODLA_OPISU[k === 'wejscie' ? kluczWejscia(poczatkowe) : k]
                            return {
                              wartosc: k,
                              tresc: <><span className="kafel-ikona" aria-hidden="true"><Ikona nazwa={z.ikona} rozmiar={18} grubosc={2} /></span>
                                <strong>{z.nazwa}</strong><span className="kafel-opis">{z.opis}</span></>,
                            }
                          })} />
              {zrobProjekt && (
                <Link to={ZROB_PROJEKT.adres} className="kafel-wyboru kafel-wejscia" onClick={() => krok('projekt3d')}>
                  <span className="kafel-ok" aria-hidden="true"><Ikona nazwa="strzalka" rozmiar={12} grubosc={3} /></span>
                  <span className="kafel-ikona" aria-hidden="true"><Ikona nazwa="projekt" rozmiar={18} grubosc={2} /></span>
                  <strong>{ZROB_PROJEKT.nazwa}</strong><span className="kafel-opis">{ZROB_PROJEKT.opis}</span>
                </Link>
              )}
            </div>
          </fieldset>
        )}

        {/* „Kilka pytań”: prosty dobór osadzony w formularzu, po „Dalej” zwija się do podsumowania. */}
        {zrodlo === 'pytania' && !gotowyOpis && !przyklad && (
          <section className="kilka-pytan" aria-label="Kilka pytań">
            <Suspense fallback={<p className="pole-podpowiedz" role="status">Wczytuję pytania…</p>}>
              <KilkaPytan trybZlecenia onGotowe={gotowePytania} poczatkowe={szkic?.odpowiedzi || dobor?.odpowiedzi || null} />
            </Suspense>
            {szkic && (
              <button type="button" className="btn btn-obrys btn-m kilka-pytan-wroc" onClick={() => setEdytujPytania(false)}>
                Zostaw poprzednie odpowiedzi
              </button>
            )}
            {proba && braki.pytania && <Blad>Odpowiedz na pytania i przejdź dalej.</Blad>}
          </section>
        )}

        {/* „Wiem, czego chcę” (2026-10-29): plan na jednym ekranie, końcówka od razu pod nim. */}
        {zrodlo === 'wiem' && (
          <section className="karta-tresc" aria-label="Twój plan" style={{ padding: 0 }}>
            <fieldset className="grupa">
              <legend>Czego potrzebujesz?</legend>
              <p className="grupa-opis">Zaznacz jedno albo kilka.</p>
              <div role="group" aria-label="Czego potrzebujesz" className="opcje-zrodla">
                {CZESCI_PLANU.map((c) => {
                  const on = planCzesci.includes(c.klucz)
                  return (
                    <button key={c.klucz} type="button" className={`kafel-wyboru${on ? ' on' : ''}`} aria-pressed={on}
                            onClick={() => setPlanCzesci((cz) => (cz.includes(c.klucz) ? cz.filter((x) => x !== c.klucz) : [...cz, c.klucz]))}>
                      <span className="kafel-ok" aria-hidden="true"><Ikona nazwa="ok" rozmiar={12} grubosc={3} /></span>
                      <span className="kafel-ikona" aria-hidden="true"><FontAwesomeIcon icon={c.ikona} /></span>
                      <strong>{c.nazwa}</strong><span className="kafel-opis">{c.opis}</span>
                    </button>
                  )
                })}
              </div>
              {proba && brakCzesci && <Blad>Zaznacz, czego potrzebujesz.</Blad>}
            </fieldset>
            <fieldset className="grupa">
              <legend>Twój plan</legend>
              <div className="pola">
                {planModuly && (
                  <label className="pole" htmlFor={`${id}-kwp`}>Moc instalacji [kWp]
                    <input className="wejscie" id={`${id}-kwp`} inputMode="decimal" placeholder="np. 8" value={planKwp}
                           onChange={(e) => setPlanKwp(e.target.value)} aria-invalid={proba && brakMocy} />
                  </label>
                )}
                {planMagazyn && (
                  <label className="pole" htmlFor={`${id}-kwh`}>Pojemność magazynu [kWh]
                    <input className="wejscie" id={`${id}-kwh`} inputMode="decimal" placeholder="np. 10" value={planKwh}
                           onChange={(e) => setPlanKwh(e.target.value)} aria-invalid={proba && brakPojemnosci} />
                  </label>
                )}
              </div>
              {proba && (brakMocy || brakPojemnosci) && (
                <Blad>{brakMocy && brakPojemnosci ? 'Podaj moc instalacji i pojemność magazynu.' : brakMocy ? 'Podaj moc instalacji.' : 'Podaj pojemność magazynu.'}</Blad>
              )}
              <p className="pole-podpowiedz">Budżet podasz niżej.</p>
            </fieldset>
            <fieldset className="grupa">
              <legend>Konkretny sprzęt <small>(opcjonalnie)</small></legend>
              {!pokazPlanSprzet ? (
                <button type="button" className="kafel-konstrukcji" onClick={() => { krok('wiem_sprzet'); setPokazPlanSprzet(true) }}>
                  <span className="kafel-ikona" aria-hidden="true"><Ikona nazwa="zestaw" rozmiar={18} grubosc={2} /></span>
                  <span className="kafel-konstrukcji-tekst"><strong>Wskaż producenta albo model</strong>
                    <span className="kafel-opis">Bez wskazania sprzęt dobierze wykonawca.</span></span>
                </button>
              ) : (
                <SprzetKlienta wartosc={planSprzet} onZmiana={setPlanSprzet} />
              )}
              {/* Konstrukcja pod modułami, falownikiem i magazynem (prośba właściciela 2026-10-30): mały kafel, po rozwinięciu
                  poszycia z obrazkami jak w Projekcie 3D. Przy samym magazynie albo falowniku dach nie ma znaczenia. */}
              {planModuly && (
                <div className="konstrukcja-planu">
                  {!pokazKonstrukcje ? (
                    <button type="button" className="kafel-konstrukcji" onClick={() => { krok('wiem_konstrukcja'); setPokazKonstrukcje(true) }}>
                      <span className="kafel-ikona" aria-hidden="true"><Ikona nazwa="dom" rozmiar={18} grubosc={2} /></span>
                      <span className="kafel-konstrukcji-tekst"><strong>Konstrukcja</strong>
                        <span className="kafel-opis">Na czym staną moduły? (opcjonalnie)</span></span>
                    </button>
                  ) : (
                    <fieldset className="grupa">
                      <legend>Konstrukcja <small>(opcjonalnie)</small></legend>
                      <div role="group" aria-label="Konstrukcja" className="kafle-konstrukcji">
                        {KONSTRUKCJE.map((k) => (
                          <button key={k.key} type="button" className={`kafel-poszycia${planKonstrukcja === k.key ? ' on' : ''}`}
                                  aria-pressed={planKonstrukcja === k.key}
                                  onClick={() => {
                                    const nowa = planKonstrukcja === k.key ? '' : k.key
                                    setPlanKonstrukcja(nowa)
                                    setDach(dachZKonstrukcji(nowa))
                                  }}>
                            <img src={obrazekKonstrukcji(k.key)} alt="" loading="lazy" />
                            <span>{k.label}</span>
                          </button>
                        ))}
                      </div>
                    </fieldset>
                  )}
                </div>
              )}
            </fieldset>
          </section>
        )}

        {gotowyOpis && zrodlo !== 'wiem' && (
          <div className="podsum" ref={podsumowanieRef}>
            <div className="podsum-glowa">
              <strong className="podsum-zrodlo">{naglowekZrodla}</strong>
              {zrodlo !== 'pytania' && <span className="wpisane"><Ikona nazwa="ok" rozmiar={14} grubosc={2.8} />Wpisane za Ciebie</span>}
            </div>
            <dl>
              {!zmienTyp && typTeraz && (
                <Wiersz etykieta="Czego potrzebujesz" wartosc={nazwaTypu(typTeraz)}
                        onZmien={zrodlo === 'pytania' ? null : () => setZmienTyp(true)} />
              )}
              {podsumowanie.map(([e, w]) => (zrodlo === 'pytania' && !przyklad && EDYTOWALNE[e] && o[EDYTOWALNE[e].klucz]
                ? <WierszLiczby key={e} etykieta={e} {...EDYTOWALNE[e]} wartosc={o[EDYTOWALNE[e].klucz]}
                                onZapisz={(v) => { krok(`popraw:${EDYTOWALNE[e].klucz}`); setPoprawki((p) => ({ ...p, [EDYTOWALNE[e].klucz]: v })) }} />
                : <Wiersz key={e} etykieta={e} wartosc={w} />))}
              {bezMagazynu && <Wiersz etykieta="Magazyn" wartosc="Bez magazynu w zleceniu" />}
              {opcja && !pokazSprzet && (
                <Wiersz etykieta="Sprzęt" wartosc={opcja.nazwa} onZmien={konkretny ? () => setZmienSprzet(true) : null} />
              )}
              {teczkaZlecenia && (
                <Wiersz etykieta="Licznik i rozdzielnica"
                        wartosc={maMiejsca(teczkaZlecenia.miejsca) ? 'Uzupełnione w teczce' : 'Nie uzupełnione, wykonawcy sprawdzą na miejscu'} />
              )}
            </dl>
            {/* Zestaw z propozycji albo sprzęt klienta z odpowiedzi: kafelki jak u wykonawcy, bez ponownego doboru. */}
            {kafleSprzetu && <div className="podsum-kafle"><SprzetZlecenia o={{ opis: o }} /></div>}
            {zrodlo === 'pytania' && !przyklad && (
              <div className="podsum-stopka">
                <div className="flex flex-wrap gap-2">
                  <button type="button" className="btn btn-obrys btn-m" onClick={() => { krok('zmien_odpowiedzi'); setEdytujPytania(true) }}>
                    Zmień odpowiedzi
                  </button>
                </div>
              </div>
            )}
            {zrodlo === 'projekt' && !przyklad && (
              <div className="podsum-stopka">
                {/* Dane projektu zmieniamy tylko w Projekcie 3D (F9), stąd jedno wyjście zamiast pól do poprawiania. */}
                <p className="pole-podpowiedz">
                  Ze zleceniem pójdzie teczka instalacji: rzut dachu, licznik i rozdzielnica. Wykonawcy zobaczą ją bez adresu i mapy.
                </p>
                <div className="flex flex-wrap gap-2">
                  <Link to="/projekt-3d" className="btn btn-obrys btn-m">
                    Popraw w Projekcie 3D<Ikona nazwa="strzalka" rozmiar={16} grubosc={2.4} />
                  </Link>
                  {teczkaZlecenia && (
                    <button type="button" className="btn btn-obrys btn-m" onClick={() => setPodglad(true)}>Co zobaczą wykonawcy</button>
                  )}
                  {/* G3, G6: prosty audyt w oknie bez wychodzenia ze zlecenia. Migawka teczki powstaje przy wystawieniu. */}
                  {teczkaZlecenia && (
                    <button type="button" className="btn btn-obrys btn-m" onClick={() => setOknoMiejsc(true)}>
                      {maMiejsca(teczkaZlecenia.miejsca) ? 'Zmień prosty audyt' : 'Uzupełnij prosty audyt'}
                    </button>
                  )}
                </div>
                {teczkaZlecenia && (
                  <p className="pole-podpowiedz">
                    Wskaż, gdzie jest licznik, rozdzielnica i którędy można poprowadzić kable. Wykonawca wyceni dokładniej
                    i bez dodatkowej wizyty.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {gotowyOpis && zrodlo !== 'pytania' && zmienTyp && (
          <fieldset className="grupa">
            <legend>Czego potrzebujesz?</legend>
            <div role="group" aria-label="Czego potrzebujesz" className="chipy-wybor">
              {TYPY_OGLOSZENIA.map((t) => <Chip key={t.klucz} on={typTeraz === t.klucz} onClick={() => wybierzTyp(t.klucz)}>{t.nazwa}</Chip>)}
            </div>
            {konflikt && (
              <PytanieOTyp zrodlo={klucz} typZrodla={typZrodla} nowy={konflikt} maMagazyn={zrodloMaMagazyn}
                           onMagazyn={rozstrzygnijMagazyn}
                           onZostaw={() => { setKonflikt(null); setTypNadpisany(null); setBezMagazynu(false); setZmienTyp(false) }}
                           onPytania={() => wybierzZrodlo('pytania')} />
            )}
            {proba && braki.typ && <Blad>Wybierz, czego potrzebujesz.</Blad>}
          </fieldset>
        )}

        {pokazSprzet && (
          <fieldset className="grupa">
            <legend>Kto zajmie się sprzętem?</legend>
            <p className="grupa-opis">Wykonawcy wycenią ten sprzęt. Zdecyduj, kto go kupi.</p>
            <WyborSprzetuZlecenia wartosc={sprzetKonkretny} onZmiana={ustawSprzet} blad={proba && braki.sprzet} />
            {proba && braki.sprzet && <Blad>Wybierz jedną z dwóch opcji.</Blad>}
          </fieldset>
        )}

        {gotowyOpis && (
          <>
            <fieldset className="grupa">
              <legend>Gdzie?</legend>
              <div className="pola">
                <WyborWojewodztwa wartosc={wojewodztwo} onZmiana={setWojewodztwo} blad={proba && !wojewodztwo} />
                <label className="pole" htmlFor={`${id}-m`}>Miejscowość
                  <input className="wejscie" id={`${id}-m`} value={miejscowosc} onChange={(e) => setMiejscowosc(e.target.value)}
                         autoComplete="address-level2" placeholder="np. Kraśnik" aria-invalid={proba && !miejscowosc.trim()} />
                </label>
              </div>
              {proba && braki.gdzie && <Blad>Podaj województwo i miejscowość.</Blad>}
            </fieldset>

            <BudzetZlecenia wartosc={budzet} zwykle={zwykle} podstawa={podstawaCeny} zOdpowiedzi={budzetZOdpowiedzi && !budzetRecznie}
                            onZmiana={(v, recznie) => { setBudzet(v); if (recznie) setBudzetRecznie(true) }} />

            {cenaOferty && (
              <label className="zgoda">
                <input type="checkbox" checked={pokazCene} onChange={(e) => setPokazCene(e.target.checked)} />
                <span>Pokaż wykonawcom cenę oferty, którą już mam ({zl(cenaOferty)} brutto). Łatwiej im będzie zaproponować lepszą.</span>
              </label>
            )}

            <div>
              <button type="button" className="wiecej-btn" aria-expanded={wiecej} aria-controls={`${id}-w`} onClick={() => { if (!wiecej) krok('wiecej'); setWiecej((w) => !w) }}>
                <span>
                  <strong>Więcej informacji dla wykonawców <small>(opcjonalnie)</small></strong>
                  <small>Budynek, {dachWWiecej ? 'dach, termin' : 'termin'} i dotacja PME2</small>
                </span>
                <Ikona nazwa="dol" rozmiar={18} grubosc={2.4} />
              </button>
              {wiecej && (
                <div id={`${id}-w`} className="karta-tresc" style={{ padding: '18px 0 0' }}>
                  <fieldset className="grupa">
                    <legend>Budynek <small>(opcjonalnie)</small></legend>
                    <div role="group" aria-label="Budynek" className="chipy-wybor">
                      {BUDYNKI_RYNKU.map((b) => <Chip key={b} on={budynek === b} onClick={() => setBudynek(budynek === b ? '' : b)}>{b}</Chip>)}
                    </div>
                  </fieldset>
                  {dachWWiecej}
                  <fieldset className="grupa">
                    <legend>Kiedy?</legend>
                    <div role="group" aria-label="Termin" className="chipy-wybor">
                      {TERMINY.map((t) => <Chip key={t.klucz} on={termin === t.klucz} onClick={() => setTermin(t.klucz)}>{t.nazwa}</Chip>)}
                    </div>
                    <label className="zgoda" style={{ marginTop: 12 }}>
                      <input type="checkbox" checked={pme2} onChange={(e) => setPme2(e.target.checked)} />
                      <span>Chcę skorzystać z dotacji PME2 na magazyn energii</span>
                    </label>
                  </fieldset>
                </div>
              )}
            </div>

            <fieldset className="grupa">
              <legend>Ile ofert chcesz dostać?</legend>
              <p className="grupa-opis">
                Handlowcy do Ciebie nie dzwonią: pokażemy Ci tylu najlepiej dopasowanych wykonawców, a Ty wybierzesz,
                z kim chcesz rozmawiać.
              </p>
              <WyborKafla nazwa={`${id}-ile`} className="liczby-firm" etykieta="Ile ofert chcesz dostać" blad={proba && braki.liczba}
                          wartosc={liczbaFirm ?? ''} onZmiana={(n) => { krok(`oferty:${n}`); setLiczbaFirm(Number(n)) }}
                          opcje={LICZBY_OFERT.map(({ n, opis: op }) => ({
                            wartosc: n,
                            tresc: <><strong>{n}<span className="sr-only"> ofert</span></strong>
                              <span className="lf-kropki" aria-hidden="true">{Array.from({ length: n }, (_, i) => <i key={i} />)}</span>
                              <span className="kafel-opis">{op}</span></>,
                          }))} />
              {proba && braki.liczba && <Blad>Wybierz, ile ofert chcesz dostać.</Blad>}
            </fieldset>

            {/* Komentarz na końcu każdej drogi, widoczny od razu (decyzja właściciela 2026-10-29). */}
            <label className="pole" htmlFor={`${id}-k`}><span>Komentarz do zlecenia <small>(opcjonalnie)</small></span>
              <textarea className="wejscie" id={`${id}-k`} rows={3} maxLength={MAKS_KOMENTARZA} value={komentarz}
                        onChange={(e) => setKomentarz(e.target.value)}
                        placeholder="np. Dach dwuspadowy na południe, chcę zasilanie awaryjne lodówki i pieca." />
              <small style={{ alignSelf: 'flex-end' }}>{komentarz.length}/{MAKS_KOMENTARZA}</small>
            </label>

            <div className="form-stopka">
              <button type="submit" className="btn btn-glowny btn-l">Wystaw zlecenie<Ikona nazwa="strzalka" rozmiar={18} grubosc={2.4} /></button>
              <p className="form-uwaga">W następnym kroku podasz adres i telefon. Zobaczy je tylko wykonawca, którego wybierzesz.</p>
            </div>
          </>
        )}
      </form>

      {podglad && <PodgladDlaWykonawcow onZamknij={zamknijPodglad} />}
      {oknoMiejsc && teczkaZlecenia && (
        <OknoMiejsc teczka={teczkaZlecenia} zapis={zapisTeczki} zapiszCzesc={zapiszCzesc} onZamknij={zamknijMiejsca}
                    zMagazynem={typTeraz === 'pv_magazyn' || typTeraz === 'magazyn'} />
      )}

      {okno && (
        <OknoZlecenia etykieta={`${id}-h`} onZamknij={zamknijOkno}>
          <form onSubmit={wyslij} noValidate>
            <div className="dialog-glowa">
              <span className="wynik-ikona"><Ikona nazwa="tarcza" rozmiar={24} grubosc={2} /></span>
              <div>
                <h3 id={`${id}-h`}>Adres i kontakt tylko dla wybranego wykonawcy</h3>
                <p>Ostatni krok przed wystawieniem zlecenia.</p>
              </div>
              <button type="button" className="dialog-x" onClick={zamknijOkno} aria-label="Zamknij"><Ikona nazwa="x" rozmiar={18} grubosc={2.4} /></button>
            </div>
            <ul className="dialog-fakty">
              <li><Ikona nazwa="ok" rozmiar={16} grubosc={2.6} /><span>Wykonawcy widzą tylko <b>miejscowość i opis zlecenia</b>.</span></li>
              <li><Ikona nazwa="ok" rozmiar={16} grubosc={2.6} /><span>Adres i telefon dostanie <b>tylko wykonawca, którego wybierzesz</b>.</span></li>
              <li><Ikona nazwa="ok" rozmiar={16} grubosc={2.6} /><span>Nie przekażemy ich innym wykonawcom ani doradcom handlowym. <b>Nie sprzedajemy kontaktów.</b></span></li>
              <li><Ikona nazwa="ok" rozmiar={16} grubosc={2.6} /><span>Udostępnienie <b>cofniesz w każdej chwili</b> jednym kliknięciem.</span></li>
            </ul>
            {/* C7: telefon jako główne pytanie; adres zwykle już przyszedł z Projektu 3D. */}
            <label className="pole" htmlFor={`${id}-t`}><span>Telefon dla wybranego wykonawcy <small>(opcjonalnie)</small></span>
              <input className="wejscie" id={`${id}-t`} data-autofokus type="tel" autoComplete="tel" placeholder="np. 600 100 200"
                     value={telefon} onChange={(e) => setTelefon(e.target.value)} />
            </label>
            <div className="pola pola-adres">
              <label className="pole" htmlFor={`${id}-u`}>Ulica
                <input className="wejscie" id={`${id}-u`} autoComplete="address-line1" placeholder="np. Słoneczna"
                       value={ulica} onChange={(e) => setUlica(e.target.value)} aria-invalid={probaOkna && !ulica.trim()} />
              </label>
              <label className="pole" htmlFor={`${id}-n`}>Nr domu
                <input className="wejscie" id={`${id}-n`} autoComplete="address-line2" placeholder="np. 12A"
                       value={nrDomu} onChange={(e) => setNrDomu(e.target.value)} aria-invalid={probaOkna && !nrDomu.trim()} />
              </label>
            </div>
            <p className="pole-podpowiedz">
              {adresZ3D ? 'Adres z Twojego Projektu 3D. Popraw go, jeśli trzeba. ' : ''}Na wsi bez nazw ulic wpisz nazwę miejscowości.
            </p>
            {probaOkna && brakiOkna.adres && <Blad>Podaj ulicę i numer domu. Zobaczy je tylko wybrany wykonawca.</Blad>}
            <label className="zgoda">
              <input type="checkbox" checked={zgoda} onChange={(e) => setZgoda(e.target.checked)} aria-invalid={probaOkna && !zgoda} />
              <span>{TRESC_ZGODY.zlecenie}</span>
            </label>
            {probaOkna && brakiOkna.zgoda && <Blad>Zaznacz zgodę, żeby wykonawcy mogli zobaczyć zlecenie.</Blad>}
            {gosc && (
              <KontoGoscia konto={konto} onZmiana={(k) => { setKonto(k); setBledyKontaZSerwera({}) }} bledy={bledyKonta}
                           tryb={trybKonta} onTryb={(t) => { setTrybKonta(t); setBlad('') }} komunikat={komunikatKonta}
                           onZalogowano={() => {
                             setKomunikatKonta('')
                             setBlad('')
                             // Po zalogowaniu zwykła wysyłka, jeśli adres i zgoda już są.
                             if (!Object.values(brakiOkna).some(Boolean)) wyslijTeraz(false)
                           }} />
            )}
            {blad && <p role="alert" className="blad-ramka"><ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />{blad}</p>}
            <div className="dialog-stopka">
              <button type="submit" className="btn btn-glowny btn-m" disabled={busy}>{busy ? 'Wystawiam…' : 'Wystaw zlecenie'}</button>
              <button type="button" className="link" onClick={zamknijOkno}>Wróć do zlecenia</button>
            </div>
          </form>
        </OknoZlecenia>
      )}
    </>
  )
}

export default FormularzOgloszenia
